/**
 * Cache Service - Advanced cache management for PWA
 * Provides intelligent cache invalidation, size management, and versioning
 */

export interface CacheStatus {
  size: number;
  cacheCount: number;
  version: string;
  quotaUsage?: number;
  quotaLimit?: number;
}

export interface CacheCleanupResult {
  success: boolean;
  cleanedCaches?: string[];
  freedSpace?: number;
}

export class CacheService {
  private static instance: CacheService;
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  private constructor() {
    this.initializeServiceWorker();
  }

  static getInstance(): CacheService {
    if (!CacheService.instance) {
      CacheService.instance = new CacheService();
    }
    return CacheService.instance;
  }

  private async initializeServiceWorker() {
    if ('serviceWorker' in navigator) {
      try {
        this.serviceWorkerRegistration = await navigator.serviceWorker.ready;
      } catch (error) {
        console.warn('Service Worker not available:', error);
      }
    }
  }

  /**
   * Send message to service worker and wait for response
   */
  private async sendMessage(type: string, payload?: any): Promise<any> {
    if (!this.serviceWorkerRegistration?.active) {
      throw new Error('Service Worker not available');
    }

    return new Promise((resolve, reject) => {
      const messageChannel = new MessageChannel();
      
      messageChannel.port1.onmessage = (event) => {
        resolve(event.data);
      };

      messageChannel.port1.addEventListener('messageerror', (error) => {
        reject(error);
      });

      this.serviceWorkerRegistration!.active!.postMessage(
        { type, payload },
        [messageChannel.port2]
      );

      // Timeout after 10 seconds
      setTimeout(() => {
        reject(new Error('Service Worker message timeout'));
      }, 10000);
    });
  }

  /**
   * Get current cache status including size and count
   */
  async getCacheStatus(): Promise<CacheStatus> {
    try {
      const swStatus = await this.sendMessage('CACHE_STATUS');
      
      // Get storage quota information if available
      let quotaUsage: number | undefined;
      let quotaLimit: number | undefined;
      
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        quotaUsage = estimate.usage;
        quotaLimit = estimate.quota;
      }

      return {
        ...swStatus,
        quotaUsage,
        quotaLimit
      };
    } catch (error) {
      console.warn('Failed to get cache status:', error);
      return {
        size: 0,
        cacheCount: 0,
        version: 'unknown'
      };
    }
  }

  /**
   * Perform intelligent cache cleanup
   */
  async cleanupCaches(): Promise<CacheCleanupResult> {
    try {
      const result = await this.sendMessage('CACHE_CLEANUP');
      return { success: true, ...result };
    } catch (error) {
      console.error('Cache cleanup failed:', error);
      return { success: false };
    }
  }

  /**
   * Invalidate specific cache by name pattern
   */
  async invalidateCache(cacheNamePattern: string): Promise<boolean> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        const matchingCaches = cacheNames.filter(name => 
          name.includes(cacheNamePattern)
        );

        const results = await Promise.all(
          matchingCaches.map(cacheName => 
            this.sendMessage('INVALIDATE_CACHE', { cacheName })
          )
        );

        return results.every(result => result.success);
      }
      return false;
    } catch (error) {
      console.error('Cache invalidation failed:', error);
      return false;
    }
  }

  /**
   * Preload tarot card images into cache
   */
  async preloadTarotCards(cardUrls: string[]): Promise<{ success: boolean; preloaded?: number }> {
    try {
      const result = await this.sendMessage('PRELOAD_CARDS', { cardUrls });
      return result;
    } catch (error) {
      console.error('Card preloading failed:', error);
      return { success: false };
    }
  }

  /**
   * Check if cache size is approaching limits
   */
  async isCacheNearLimit(threshold: number = 0.8): Promise<boolean> {
    try {
      const status = await this.getCacheStatus();
      if (status.quotaUsage && status.quotaLimit) {
        return (status.quotaUsage / status.quotaLimit) > threshold;
      }
      return false;
    } catch (error) {
      console.warn('Failed to check cache limits:', error);
      return false;
    }
  }

  /**
   * Get cache efficiency metrics
   */
  async getCacheMetrics(): Promise<{
    hitRate: number;
    missRate: number;
    totalRequests: number;
  }> {
    // This would require additional tracking in the service worker
    // For now, return placeholder values
    return {
      hitRate: 0,
      missRate: 0,
      totalRequests: 0
    };
  }

  /**
   * Force cache update for specific resource types
   */
  async updateCache(resourceType: 'api' | 'images' | 'cards' | 'all'): Promise<boolean> {
    try {
      const cachePatterns = {
        api: 'api-cache',
        images: 'images-cache',
        cards: 'tarot-cards-cache',
        all: ''
      };

      const pattern = cachePatterns[resourceType];
      return await this.invalidateCache(pattern);
    } catch (error) {
      console.error('Cache update failed:', error);
      return false;
    }
  }

  /**
   * Schedule periodic cache cleanup
   */
  schedulePeriodicCleanup(intervalMs: number = 24 * 60 * 60 * 1000): void {
    // Schedule cleanup every 24 hours by default
    setInterval(async () => {
      try {
        const isNearLimit = await this.isCacheNearLimit(0.7);
        if (isNearLimit) {
          console.log('Cache approaching limit, performing cleanup...');
          await this.cleanupCaches();
        }
      } catch (error) {
        console.warn('Scheduled cache cleanup failed:', error);
      }
    }, intervalMs);
  }

  /**
   * Get cache version information
   */
  async getCacheVersion(): Promise<string> {
    try {
      const status = await this.getCacheStatus();
      return status.version;
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * Check if caches need migration due to version change
   */
  async needsCacheMigration(expectedVersion: string): Promise<boolean> {
    try {
      const currentVersion = await this.getCacheVersion();
      return currentVersion !== expectedVersion;
    } catch (error) {
      return true; // Assume migration needed if we can't determine version
    }
  }

  /**
   * Perform cache migration for version updates
   */
  async migrateCaches(newVersion: string): Promise<boolean> {
    try {
      // Clean up old caches
      await this.cleanupCaches();
      
      // The service worker will handle creating new versioned caches
      console.log(`Cache migration completed to version ${newVersion}`);
      return true;
    } catch (error) {
      console.error('Cache migration failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const cacheService = CacheService.getInstance();